/**
 * The async copilot for imports — typed wrappers over `/v1/twin/copilot/threads[/{id}/messages]` and their React
 * Query hooks (Phase 5; backend `internal/twin/copilot.go`, contract `api/openapi.yaml` CopilotThread,
 * CopilotMessage; design docs/design/2026-09-24_async-copilot.md).
 *
 * What the server decides, and the UI only shows:
 *   - a thread is its creator's alone; another person's reads as not found;
 *   - a message's one file enters the import intake as a hub upload does (origin `copilot`), in the same transaction;
 *     a document family's file goes to the family's product instead, as an upload's does (the documents lane);
 *   - the brain's limits: 5 messages of a person's waiting at once (20 of an org's), 50 a day (200 an org), and 2
 *     messages with a file being sent at once; each refusal is a 429 whose message says which;
 *   - the brain answers on the brain PC, usually within a minute or two; the page polls while a message waits.
 *
 * The interactive copilot (contract v1, 2026-09-26; the server's side is arjun/feat/copilot-interactive-server): a
 * message carries `blocks`. The brain asks back (ask) and proposes changes (proposal); a person answers an ask in the
 * JSON form ({text, blocks}), and approves or rejects a proposal, which their browser applies through the platform's
 * own create routes, as them, then records on the proposal (`POST /v1/twin/copilot/proposals/{id}/outcome`).
 *
 * Asked from a page (2026-09-28, lib/copilot/ask): a person's message may carry where they asked from, a context
 * block, in the JSON form beside their answer.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { IMPORT_JOBS_KEY, type ImportJob, type RoutedDocument } from "@/lib/api/imports";
import { alarms } from "@/lib/api/alarms";
import { dashboards, toDashboardConfig } from "@/lib/api/dashboards";
import { errorCodeOf, statusOf } from "@/lib/api/error";
import { fetchAllPages } from "@/lib/api/paginate";
import { ORG_TAG_CATALOG_KEY } from "@/lib/api/queries";
import { listOrgTags } from "@/lib/api/twin-bindings";
import { twinCorrelationGroups } from "@/lib/api/twin-correlation-groups";
import type { AlarmRuleDTO, Dashboard } from "@/lib/api/types";
import type { DashboardConfig } from "@/lib/dashboard/types";
import type { RecommendationEvent } from "@/lib/api/recommendations";
import type { QuestionStatus } from "@/lib/api/twin-knowledge";
import { copilotAvailabilityOf, type CopilotAvailability } from "@/lib/copilot/availability";
import type { CopilotAbout } from "@/lib/copilot/ask";
import { MESSAGES_PAGE, awaitingBrain, mergeFrom, pollAfter, pollInterval } from "@/lib/imports/copilot";
import { blocksOf } from "@/lib/imports/copilot-blocks";
import {
  PROCESS_WINDOW_PARAMETERS_KEY,
  PROCESS_WINDOWS_KEY,
  SCRAP_TARGETS_KEY,
  fetchProcessWindowParameters,
  fetchProcessWindows,
  fetchScrapTargets,
  saveProcessWindow,
  saveScrapTarget,
  windowsSaved,
} from "@/lib/api/limits";
import { limitsFoldOf } from "@/lib/brief/limits";
import {
  acquisitionStudyKey,
  twinAcquisition,
  type AcquisitionDecisionKind,
  type AcquisitionEdit,
} from "@/lib/api/acquisition";
import { putInChunks } from "@/lib/acquisition/review";
import { decisionsHeld } from "@/lib/acquisition/consultant";
import {
  cardUnitsOf,
  limitIdOf,
  proposalStateOf,
  sameGroup,
  sameRule,
  sameTarget,
  sameWindow,
  withProposalState,
  type CardUnits,
  type CreatePlan,
  type ProposalDeps,
  type TagWords,
} from "@/lib/imports/copilot-proposals";
import { BoardChanged, addTrendWidget, findTrendWidget, type BoardCalls } from "@/lib/imports/copilot-widget";
import { addBoardWidgets, findBoardWidgets, isBoardPlan } from "@/lib/imports/copilot-board";

export interface CopilotThread {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  /** The thread's messages waiting for, or with, the brain. */
  open: number;
}

/**
 * A message's file: the import job it opened (or found in flight), or, for a document family's file whose org holds the
 * family's product (the documents lane, migration 0228), the document it became and the product it went to. Never
 * both: a sent file has no job.
 */
export interface CopilotAttachment {
  job_id?: string;
  document_id?: string;
  filename: string;
  /** With document_id: the product the file went to. It is not imported. */
  routed?: { product: string; product_name: string };
}

export interface CopilotMessage {
  id: string;
  thread_id: string;
  role: "user" | "brain";
  body: string;
  attachments: CopilotAttachment[];
  /** A person's message: pending, claimed, answered or failed. The brain's: none. */
  status: "pending" | "claimed" | "answered" | "failed" | "none";
  error?: string;
  reply_to?: string;
  created_at: string;
  /**
   * The interactive copilot's blocks (contract v1, 2026-09-26): a brain message's asks and proposals, a person's
   * answer. Always an array from a server that has them; absent from an older one. Read through blocksOf
   * (lib/imports/copilot-blocks), which drops a block it cannot show.
   */
  blocks?: CopilotBlock[];
}

// ── The interactive copilot's blocks (contract v1, 2026-09-26) ─────────────────────────────────────────────────────

/** A choice the brain offers, and a select input's option. */
export interface CopilotChoice {
  value: string;
  label: string;
}

export type CopilotInputKind = "date" | "date_range" | "number" | "text" | "select";

/** One of an ask's inputs. `options` belong to a select; `min`, `max` and `unit` to a number. */
export interface CopilotAskInput {
  /** `^[a-z0-9_]{1,32}$`, never "choice": the key of its value in an answer. */
  name: string;
  kind: CopilotInputKind;
  label: string;
  options?: CopilotChoice[];
  min?: number;
  max?: number;
  unit?: string;
}

/** The brain asks back when a request lacks what it needs: choices (0–8), inputs (0–3), at least one of either. */
export interface CopilotAskBlock {
  type: "ask";
  id: string;
  question: string;
  choices: CopilotChoice[];
  /** The person may pick several choices. */
  multi?: boolean;
  inputs: CopilotAskInput[];
}

export type CopilotProposalKind =
  | "correlation_group"
  | "alarm_rule"
  | "report"
  | "dashboard_widget"
  | "process_window"
  | "scrap_target"
  | "acquisition_change";
export type CopilotProposalStatus = "proposed" | "applied" | "failed" | "rejected";

/**
 * What an applied proposal made: its id, and the app path that shows it, and, when applying did less than the card
 * holds, a line of words on what it did and skipped (`summary`: an acquisition_change card applies only the rows still
 * undecided, and keeps saying how many); or, on a rejected one, why the person declined it, when they said (L2).
 */
export interface CopilotProposalResult {
  id?: string;
  url?: string;
  summary?: string;
  reason?: string;
}

export interface CorrelationGroupSpec {
  name: string;
  mechanism?: string;
  input_params: string[];
  outcome_defects: string[];
}

export interface AlarmRuleSpec {
  name: string;
  tag_id: string;
  op: "gt" | "lt";
  value: number;
  severity: "info" | "warning" | "critical";
  /**
   * The unit `value` is in (units slice 2d): a unit the person reads ("°F") when they gave the threshold in it, which
   * Approve converts to the tag's own unit. Absent, or the tag's unit, `value` is in the tag's unit, as before.
   */
  unit?: string;
}

export interface ReportSpec {
  granularity: "daily" | "weekly" | "monthly";
  /** Plant days, YYYY-MM-DD, from ≤ to. */
  from: string;
  to: string;
}

/**
 * A trend widget on a board the org has (dashboard_id), or on a new board (dashboard_id null, dashboard_name). The
 * person's browser adds it through the dashboards routes, as the Dashboards page does (lib/imports/copilot-widget).
 */
export type DashboardWidgetSpec = DashboardTrendSpec | DashboardBoardSpec;

/** v1 (2026-09-26): one trend of 1 to 4 of the org's tags. */
export interface DashboardTrendSpec {
  dashboard_id: string | null;
  dashboard_name?: string;
  widget: { kind: "trend"; title: string; tag_ids: string[]; range: "24h" | "7d" | "30d" | "1y" };
}

/**
 * v2 (B6, 2026-10-19; the server's copilot_board.go): a board of 1 to 12 widgets with one approval, on a new board or
 * appended below the widgets of a board the org has. A port source binds by machine and port (alias: the machine's
 * entity id); a tag source by the org's tag ids. A pareto binds nothing (the plant's scrap by defect).
 */
export interface DashboardBoardSpec {
  dashboard_id: string | null;
  dashboard_name?: string;
  /**
   * A machine's board (T4): the machine the consultant named; the platform composed `widgets` from its roles when it
   * stored the card (the machine page's suggested board), never the model.
   */
  machine?: string;
  widgets: DashboardBoardWidgetSpec[];
}

export interface DashboardBoardWidgetSpec {
  kind: "kpi" | "timeseries" | "status-grid" | "histogram" | "table" | "spc" | "pareto";
  title: string;
  source?: { kind: "port"; alias: string; slots: Record<string, string[]> } | { kind: "tag"; slots: Record<string, string[]> };
  range?: "24h" | "7d" | "30d" | "1y";
  /** Plant days, inclusive, at most 400. */
  window?: { from: string; to: string };
  /** The machine's yardstick (a port source's), or ends the card gives. */
  band?: { yardstick: true } | { lo?: number; hi?: number; unit?: string; label?: string };
}

/** A plant's process window (the Plant limits page): its input and grade, and an end or two. */
export interface ProcessWindowSpec {
  condition: string;
  grade?: string;
  lo?: number;
  hi?: number;
  /** The unit lo and hi are in: the parameter's stored unit, or (units slice 2d) one the person reads, which Approve converts. */
  unit?: string;
  note?: string;
}

/** A plant's scrap target: the total's or a defect's, as a fraction of castings. */
export interface ScrapTargetSpec {
  kind: "total" | "defect";
  defect?: string;
  target_rate: number;
  note?: string;
}

/**
 * One change of an acquisition_change card (Acquisition P3c): a decision on a row of an Acquisition study, as PUT
 * .../decisions takes one (`edited` with an edit only, canonical as the server stored it), and why.
 */
export interface AcquisitionChange {
  point_key: string;
  decision: AcquisitionDecisionKind;
  edited?: AcquisitionEdit;
  reason: string;
}

/**
 * A set of row decisions on a proposed Acquisition study (Acquisition P3c; the server's copilot_acquisition.go): 1 to 50
 * changes, each checked by the very validator a person's decisions pass. An org admin applies them, as theirs, through
 * PUT /v1/twin/acquisition/studies/{id}/decisions (lib/acquisition/consultant decides which go).
 */
export interface AcquisitionChangeSpec {
  study_id: string;
  /**
   * When the claim the reply answers read the study (the platform sets it when it stores the reply; the consultant
   * cannot): the card is compared against the study as of then. Absent on a card made before the stamp, which reads as
   * of its message's time (lib/acquisition/consultant cardTime).
   */
  read_at?: string;
  changes: AcquisitionChange[];
}

/**
 * The live state the server merges into a proposal block on every read, and what the outcome route returns
 * ({proposal}). `decided_at` is set exactly when the status is not proposed.
 */
export interface CopilotProposalState {
  proposal_id: string;
  kind: CopilotProposalKind;
  status: CopilotProposalStatus;
  result?: CopilotProposalResult | null;
  error?: string | null;
  decided_at?: string | null;
}

interface CopilotProposalBase {
  type: "proposal";
  id: string;
  title: string;
  summary?: string;
  /** Added by the server when it stores the brain's message: the id the outcome route takes. */
  proposal_id?: string;
  status?: CopilotProposalStatus;
  result?: CopilotProposalResult | null;
  error?: string | null;
  decided_at?: string | null;
}

/** A change the brain proposes: a person approves it on its card, and their browser applies it, as them. */
export type CopilotProposalBlock =
  | (CopilotProposalBase & { kind: "correlation_group"; spec: CorrelationGroupSpec })
  | (CopilotProposalBase & { kind: "alarm_rule"; spec: AlarmRuleSpec })
  | (CopilotProposalBase & { kind: "report"; spec: ReportSpec })
  | (CopilotProposalBase & { kind: "dashboard_widget"; spec: DashboardWidgetSpec })
  | (CopilotProposalBase & { kind: "process_window"; spec: ProcessWindowSpec })
  | (CopilotProposalBase & { kind: "scrap_target"; spec: ScrapTargetSpec })
  | (CopilotProposalBase & { kind: "acquisition_change"; spec: AcquisitionChangeSpec });

export interface CopilotDateRange {
  from: string;
  to: string;
}

/** An answer's values: `choice` (the ask's choice values), and a value per input name, of the input's kind. */
export interface CopilotAnswerValues {
  choice?: string[];
  [input: string]: string | number | CopilotDateRange | string[] | undefined;
}

/** A person's answer to an ask of an earlier brain message in the same thread. */
export interface CopilotAnswerBlock {
  type: "answer";
  message_id: string;
  ask_id: string;
  values: CopilotAnswerValues;
}

/**
 * Where a person asked from (2026-09-28): the page, and what on it the question is about (lib/copilot/ask). The first
 * message a person sends after following a page's "Ask" link carries it, beside an answer when they answer an ask; the
 * server checks it (copilot_context.go) and hands the brain the briefs of the periods it names.
 */
export interface CopilotContextBlock extends CopilotAbout {
  type: "context";
}

/** The blocks a person's message carries: at most one answer, and at most one context. */
export type CopilotPersonBlock = CopilotAnswerBlock | CopilotContextBlock;

/**
 * A brain reply's correction card (L2, the 2026-10-01 learning-loop plan): a recommendation the person said is wrong,
 * or does not apply, by its key, with their reason quoted from their own words. `text` is the recommendation as the
 * platform words it, never the brain's; `state` is the key's latest event, merged by the server on every read. The
 * person records it as themselves ("Record this correction": POST /v1/twin/recommendation-events, via
 * copilot:<thread>); the consultant records nothing.
 */
export interface CopilotCorrectionBlock {
  type: "correction";
  id: string;
  key: string;
  kind: "wrong" | "not_applicable";
  reason: string;
  text?: string;
  state?: RecommendationEvent | null;
}

/**
 * A brain reply's "Record as answer" card (L2): the person's words, quoted, read as the answer to an open plant
 * question. `topic`, `status` and `answered_at` are the question's, from the server. The person may edit the words and
 * records them as themselves (POST /v1/twin/knowledge/questions/{id}/answer).
 */
export interface CopilotPlantAnswerBlock {
  type: "plant_answer";
  id: string;
  question_id: string;
  text: string;
  topic?: string;
  status?: QuestionStatus;
  answered_at?: string | null;
}

export type CopilotBlock =
  | CopilotAskBlock
  | CopilotProposalBlock
  | CopilotAnswerBlock
  | CopilotContextBlock
  | CopilotCorrectionBlock
  | CopilotPlantAnswerBlock;

/**
 * What a person's browser records after acting on a proposal. `applied` and `rejected` are final; `failed` is not, so
 * the person can try again and record `applied`, or give up with `rejected`. `result` goes with applied only, `error`
 * (≤ 500) with failed only, and `reason` (≤ 500, optional: why they declined it, L2) with rejected only: the type
 * allows nothing else.
 */
export type CopilotProposalOutcome =
  | { status: "applied"; result: CopilotProposalResult }
  | { status: "failed"; error: string }
  | { status: "rejected"; reason?: string };

/** The most characters a rejection's reason keeps (copilotRejectReasonMax). */
export const REJECT_REASON_MAX = 500;

export interface CopilotMessagePosted {
  message: CopilotMessage;
  /** The job the message's file opened or found in flight; none for a file sent to a product. */
  jobs: ImportJob[];
  /** The document the message's file became (migration 0228); absent from an older server. */
  documents?: RoutedDocument[];
}

const enc = encodeURIComponent;

export const copilotImports = {
  threads: {
    list: () => api.get<{ items: CopilotThread[] }>("/v1/twin/copilot/threads?limit=20"),
    create: (title: string) => api.post<CopilotThread>("/v1/twin/copilot/threads", { title }),
  },
  messages: {
    /** One page of a thread's messages after `after` (from the start without it): at most 500, oldest first. */
    list: (threadId: string, after?: string | null) =>
      api.get<{ items: CopilotMessage[] }>(
        `/v1/twin/copilot/threads/${enc(threadId)}/messages${after ? `?after=${enc(after)}` : ""}`,
      ),
    /** Every message after `after`, page by page (#843 review HIGH-2: one page stops at 500). */
    listFrom: async (threadId: string, after: string | null): Promise<CopilotMessage[]> => {
      const out: CopilotMessage[] = [];
      let cursor = after;
      for (;;) {
        const page = await copilotImports.messages.list(threadId, cursor);
        out.push(...page.items);
        if (page.items.length < MESSAGES_PAGE) return out;
        cursor = page.items[page.items.length - 1].id;
      }
    },
    post: (threadId: string, text: string, files: File[]) => {
      const fd = new FormData();
      fd.append("text", text);
      for (const f of files) fd.append("files", f, f.name);
      return api.postForm<CopilotMessagePosted>(`/v1/twin/copilot/threads/${enc(threadId)}/messages`, fd);
    },
    /**
     * The JSON form, {text, blocks?}: a starter, or a person's answer to an ask, and where they asked from (a context
     * block, after the answer). It takes no file; the composer sends a file in the multipart form above, which
     * carries no block.
     */
    postJson: (threadId: string, text: string, blocks?: CopilotPersonBlock[]) =>
      api.post<CopilotMessagePosted>(
        `/v1/twin/copilot/threads/${enc(threadId)}/messages`,
        blocks && blocks.length > 0 ? { text, blocks } : { text },
      ),
  },
};

/** An outcome's error as the server keeps it: trimmed, at most 500 characters. */
function outcomeBody(outcome: CopilotProposalOutcome): CopilotProposalOutcome {
  switch (outcome.status) {
    case "applied":
      return { status: "applied", result: outcome.result };
    case "failed":
      return { status: "failed", error: [...outcome.error.trim()].slice(0, 500).join("") };
    case "rejected": {
      const reason = [...(outcome.reason ?? "").trim()].slice(0, REJECT_REASON_MAX).join("");
      return reason ? { status: "rejected", reason } : { status: "rejected" };
    }
  }
}

/**
 * Records what became of a proposal, as the person (`POST /v1/twin/copilot/proposals/{id}/outcome`): applied with
 * what it made, failed with why, or rejected. `applied` and `rejected` are final: a later outcome answers 409
 * `proposal_decided`. Returns {proposal}, the state a proposal block carries.
 */
export function recordProposalOutcome(proposalId: string, outcome: CopilotProposalOutcome) {
  return api.post<{ proposal: CopilotProposalState }>(
    `/v1/twin/copilot/proposals/${enc(proposalId)}/outcome`,
    outcomeBody(outcome),
  );
}

/** Build-time flag: live fetches only when explicitly "true". */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const COPILOT_THREADS_KEY = ["copilot-import-threads"] as const;
export const copilotMessagesKey = (threadId: string) => ["copilot-import-messages", threadId] as const;

export function useCopilotThreadsQuery() {
  return useQuery({
    queryKey: COPILOT_THREADS_KEY,
    queryFn: () => copilotImports.threads.list(),
    enabled: useLiveEnabled(),
    staleTime: 30_000,
  });
}

/**
 * Whether the AI Casting Consultant is open for this person here (lib/copilot/availability), from the same thread
 * list read the conversation makes (one cached read serves the top bar, the page and the import hub).
 */
export function useCopilotAvailability(): CopilotAvailability {
  const live = useLiveEnabled();
  const threads = useCopilotThreadsQuery();
  return copilotAvailabilityOf({ live, isSuccess: threads.isSuccess, error: threads.error });
}

export function useCreateCopilotThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => copilotImports.threads.create(title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COPILOT_THREADS_KEY });
    },
  });
}

/**
 * A thread as the page reads it: what the cache holds, with what may have changed since (pollAfter: from the first
 * message still waiting, or anything new) read from the server and spliced in. The whole thread on the first read.
 */
async function readThread(qc: QueryClient, threadId: string): Promise<{ items: CopilotMessage[] }> {
  const had = qc.getQueryData<{ items: CopilotMessage[] }>(copilotMessagesKey(threadId))?.items ?? [];
  const after = pollAfter(had);
  return { items: mergeFrom(had, after, await copilotImports.messages.listFrom(threadId, after)) };
}

/** The part of a thread query the polling reads: its messages, and whether the last read failed. */
type ThreadPollState = { state: { data?: { items: CopilotMessage[] }; status: "pending" | "success" | "error" } };

/**
 * How a thread polls (useCopilotMessagesQuery). While a message waits it polls, backing off (pollInterval), and goes on
 * polling while the page is out of view: a person who asks and then looks at another tab comes back to the answer, not
 * to "Waiting for the consultant" (React Query pauses an interval in a hidden page unless told otherwise, and the app
 * turns reading on focus off). The moment the page is back it reads again, but only while a message waits; a thread
 * with nothing waiting is not read on return, as before.
 */
export const threadPolling = {
  refetchInterval: (q: ThreadPollState) => pollInterval(q.state.data?.items ?? [], Date.now(), q.state.status === "error"),
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: (q: ThreadPollState) => awaitingBrain(q.state.data?.items ?? []),
};

/**
 * A thread's messages (readThread). It polls while a message waits, backing off from 4 s to 60 s, and stops once all
 * are answered (pollInterval). A poll that fails keeps the thread (React Query keeps the data), and while a message
 * waits the next poll comes 60 s later, so the page recovers by itself once the API is back (#843 review N1).
 */
export function useCopilotMessagesQuery(threadId: string | null) {
  const live = useLiveEnabled();
  const qc = useQueryClient();
  return useQuery({
    queryKey: copilotMessagesKey(threadId ?? ""),
    queryFn: () => readThread(qc, threadId as string),
    enabled: live && !!threadId,
    staleTime: 2_000,
    ...threadPolling,
  });
}

/**
 * Reads a thread again after a send got no answer (#868 review): the server may have kept the message before the
 * connection dropped. One attempt, into the cache, so a message found shows in the thread and the page polls for its
 * reply; the threads and the jobs refresh, as after a send. Gives the thread as the page then knows it: what it had,
 * when this read failed too (the thread then shows why, above it). It never throws.
 */
export function useRecheckThread(): (threadId: string) => Promise<CopilotMessage[]> {
  const qc = useQueryClient();
  return async (threadId) => {
    const key = copilotMessagesKey(threadId);
    await qc.fetchQuery({ queryKey: key, queryFn: () => readThread(qc, threadId), retry: false, staleTime: 0 }).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: COPILOT_THREADS_KEY });
    void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    return qc.getQueryData<{ items: CopilotMessage[] }>(key)?.items ?? [];
  };
}

/**
 * A message to post: the composer's, in the multipart form (a file or none, as before), or words with an answer
 * block, a context block, both, or alone (a starter), in the JSON form.
 */
export type CopilotPost =
  | { threadId: string; text: string; files: File[] }
  | { threadId: string; text: string; blocks?: CopilotPersonBlock[] };

export function usePostCopilotMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CopilotPost) =>
      "files" in v
        ? copilotImports.messages.post(v.threadId, v.text, v.files)
        : copilotImports.messages.postJson(v.threadId, v.text, v.blocks),
    onSuccess: (_out, v) => {
      void qc.invalidateQueries({ queryKey: copilotMessagesKey(v.threadId) });
      void qc.invalidateQueries({ queryKey: COPILOT_THREADS_KEY });
      void qc.invalidateQueries({ queryKey: IMPORT_JOBS_KEY });
    },
  });
}

// ── The interactive copilot's proposals: applied by the person's browser, as them (contract v1, 2026-09-26) ──────

/** The correlation groups', alarm rules' and dashboards' lists, refreshed once the copilot makes one of them. */
const CORRELATION_GROUPS_KEY = ["twin-correlation-groups"] as const;
const ALARM_RULES_KEY = ["alarm-rules"] as const;
const DASHBOARDS_KEY = ["dashboards"] as const;

/**
 * Saves a board whole (PATCH /v1/iot/dashboards/{id}). With the stamp the board was read at, the platform writes it only
 * if the board is as it was read: a 409 board_changed (someone saved in between) writes nothing and is thrown as
 * BoardChanged, for the caller to read the board again. Any other failure is thrown as it came.
 */
export async function saveBoard(board: DashboardConfig, expectedUpdatedAt?: string): Promise<void> {
  try {
    await dashboards.update(board.id, {
      config: board,
      ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
    });
  } catch (err) {
    if (statusOf(err) === 409 && errorCodeOf(err) === "board_changed") throw new BoardChanged();
    throw err;
  }
}

/**
 * The dashboards routes a widget or a board is added through, as the Dashboards page adds one, as the person
 * (lib/imports/copilot-widget BoardCalls): a consultant's card and the machine page's suggested board (B6) share them.
 */
export function useBoardCalls(): BoardCalls {
  const qc = useQueryClient();
  return {
    // The org's tags, read again once ten seconds old: an approval's look for the widget and its add share one read.
    tags: async () =>
      (await qc.fetchQuery({ queryKey: ORG_TAG_CATALOG_KEY, queryFn: () => listOrgTags(), staleTime: 10_000 })).items,
    board: async (id) => toDashboardConfig(await dashboards.get(id)),
    boards: async () => (await fetchAllPages<Dashboard>("/v1/iot/dashboards")).items.map(toDashboardConfig),
    // The whole board as the config, as the Dashboards page saves one (lib/dashboard/store).
    create: async (board) => {
      await dashboards.create({ id: board.id, name: board.name, config: board });
      void qc.invalidateQueries({ queryKey: DASHBOARDS_KEY });
    },
    save: async (board, expectedUpdatedAt) => {
      await saveBoard(board, expectedUpdatedAt);
      void qc.invalidateQueries({ queryKey: DASHBOARDS_KEY });
    },
    newId: () => crypto.randomUUID(),
  };
}

/**
 * The platform calls behind a proposal's approval (lib/imports/copilot-proposals ProposalDeps, but opening a page,
 * which is the router's): the proposal's live state, from its thread read again; the create routes, as the person;
 * the lists that find a change already made (by another tab, or before an answer was lost); and the outcome route,
 * whose answer is read defensively (proposalStateOf: what was sent, when it says too little). A widget card adds its
 * trend through the Dashboards page's own routes (lib/imports/copilot-widget), awaiting each, so a board that does not
 * save is a failure the card records.
 */
export function useProposalCalls(): (block: CopilotProposalBlock, threadId: string) => Omit<ProposalDeps, "open"> {
  const qc = useQueryClient();
  const boards = useBoardCalls();
  return (block, threadId) => ({
    current: async () => {
      for (const m of await copilotImports.messages.listFrom(threadId, null)) {
        for (const b of blocksOf(m)) {
          if (b.type === "proposal" && b.proposal_id === block.proposal_id) return b.status ?? "proposed";
        }
      }
      return null;
    },
    create: async (plan: CreatePlan) => {
      // An Acquisition study's decisions, saved as the person's own through the study's route, as many a PUT as it takes.
      if (plan.kind === "acquisition_change") {
        await putInChunks(plan.decisions, (chunk) => twinAcquisition.studies.decide(plan.studyId, chunk));
        void qc.invalidateQueries({ queryKey: acquisitionStudyKey(plan.studyId) });
        return { id: plan.studyId };
      }
      if (plan.kind === "correlation_group") {
        const group = await twinCorrelationGroups.create(plan.body);
        void qc.invalidateQueries({ queryKey: CORRELATION_GROUPS_KEY });
        return { id: group.id };
      }
      // A plant limit has no id of its own: the one it is set for (its input and grade, or its defect) names it.
      if (plan.kind === "process_window") {
        qc.setQueryData(PROCESS_WINDOWS_KEY, await saveProcessWindow(plan.body));
        windowsSaved(qc);
        return { id: limitIdOf(plan) };
      }
      if (plan.kind === "scrap_target") {
        qc.setQueryData(SCRAP_TARGETS_KEY, await saveScrapTarget(plan.body));
        return { id: limitIdOf(plan) };
      }
      // A widget is added to its board, or a new board is made with it: the Dashboards page reads its list again.
      if (plan.kind === "dashboard_widget") {
        const made = isBoardPlan(plan) ? await addBoardWidgets(plan, boards) : await addTrendWidget(plan, boards);
        void qc.invalidateQueries({ queryKey: DASHBOARDS_KEY });
        return made;
      }
      const rule = await alarms.rules.create(plan.body);
      void qc.invalidateQueries({ queryKey: ALARM_RULES_KEY });
      return { id: rule.id };
    },
    find: async (plan: CreatePlan) => {
      // The decisions are there when the study's rows hold every one of them as the card sent it.
      if (plan.kind === "acquisition_change") {
        const study = await twinAcquisition.studies.get(plan.studyId);
        return decisionsHeld(plan.decisions, study.rows) ? plan.studyId : null;
      }
      if (plan.kind === "correlation_group") {
        const groups = await twinCorrelationGroups.list();
        return groups.items.find((g) => sameGroup(g, plan.body))?.id ?? null;
      }
      if (plan.kind === "process_window") {
        // A proposal may name a parameter by a name the plant records it under: the platform saved it under its own.
        const [doc, params] = await Promise.all([fetchProcessWindows(), fetchProcessWindowParameters()]);
        const fold = limitsFoldOf(params?.parameters ?? []);
        return doc?.windows.some((w) => sameWindow(w, plan.body, fold)) ? limitIdOf(plan) : null;
      }
      if (plan.kind === "scrap_target") {
        const doc = await fetchScrapTargets();
        return doc?.targets.some((t) => sameTarget(t, plan.body)) ? limitIdOf(plan) : null;
      }
      if (plan.kind === "dashboard_widget") {
        return isBoardPlan(plan) ? findBoardWidgets(plan, boards) : findTrendWidget(plan, boards);
      }
      const rules = await fetchAllPages<AlarmRuleDTO>("/v1/iot/alarms/rules");
      return rules.items.find((r) => sameRule(r, plan.body))?.id ?? null;
    },
    record: async (outcome: CopilotProposalOutcome) => {
      const id = block.proposal_id ?? "";
      const res: unknown = await recordProposalOutcome(id, outcome);
      return proposalStateOf(res, {
        proposal_id: id,
        kind: block.kind,
        status: outcome.status,
        result:
          outcome.status === "applied"
            ? outcome.result
            : outcome.status === "rejected" && outcome.reason?.trim()
              ? { reason: outcome.reason.trim() }
              : null,
        error: outcome.status === "failed" ? outcome.error : null,
        decided_at: new Date().toISOString(),
      });
    },
  });
}

/**
 * Merges a recorded proposal's state into the thread in the cache. The page polls only for new messages, so a
 * proposal's card would otherwise keep its old state until the page is loaded again.
 */
export function usePatchProposal(): (threadId: string, state: CopilotProposalState) => void {
  const qc = useQueryClient();
  return (threadId, state) => {
    qc.setQueryData<{ items: CopilotMessage[] }>(copilotMessagesKey(threadId), (d) =>
      d ? { items: withProposalState(d.items, state) } : d,
    );
  };
}

/**
 * Reads the whole thread again, into the cache: after a proposal was decided elsewhere (409), every card shows the
 * server's state. A poll in flight is cancelled first: it reads only the newest messages, and the read would join it
 * instead of reading the whole thread. A read that fails keeps the thread as it was (the next poll, or Try again,
 * reads it). Never throws.
 */
export function useReloadThread(): (threadId: string) => Promise<void> {
  const qc = useQueryClient();
  return async (threadId) => {
    await qc.cancelQueries({ queryKey: copilotMessagesKey(threadId) }).catch(() => undefined);
    await qc
      .fetchQuery({
        queryKey: copilotMessagesKey(threadId),
        queryFn: async () => ({ items: await copilotImports.messages.listFrom(threadId, null) }),
        retry: false,
        staleTime: 0,
      })
      .catch(() => undefined);
  };
}

/**
 * The org's tags by id, as words for an alarm proposal's card (its name and unit). Read only while the thread shows
 * an alarm proposal, from the org's tag list the app already caches (ORG_TAG_CATALOG_KEY); the card never waits for
 * it: the proposal's title names the tag meanwhile.
 */
export function useCopilotTagWords(wanted: boolean): Record<string, TagWords> {
  const live = useLiveEnabled();
  const catalog = useQuery({
    queryKey: ORG_TAG_CATALOG_KEY,
    queryFn: () => listOrgTags(),
    enabled: live && wanted,
    staleTime: 60_000,
  });
  return useMemo(
    () => Object.fromEntries((catalog.data?.items ?? []).map((t) => [t.id, { name: t.name, unit: t.unit }] as const)),
    [catalog.data],
  );
}

type CardLookups = Required<Pick<CardUnits, "tagUnit" | "conditionUnit">>;

/** The units a proposal card's numbers are stored in, as the page knows them, and a read of the list a card needs. */
export interface CopilotCardUnits extends CardLookups {
  /** The lookups once the list a card needs (needsStoredUnit) is read: from the cache, or read now. */
  read: (need: "tag" | "condition") => Promise<CardLookups>;
}

type OrgTags = Awaited<ReturnType<typeof listOrgTags>>;
type WindowParameters = Awaited<ReturnType<typeof fetchProcessWindowParameters>>;

const tagWordsOf = (items: OrgTags["items"]): Record<string, TagWords> =>
  Object.fromEntries(items.map((t) => [t.id, { name: t.name, unit: t.unit }] as const));

const TAGS_QUERY = { queryKey: ORG_TAG_CATALOG_KEY, queryFn: () => listOrgTags(), staleTime: 60_000 };
const PARAMETERS_QUERY = {
  queryKey: PROCESS_WINDOW_PARAMETERS_KEY,
  queryFn: fetchProcessWindowParameters,
  staleTime: 5 * 60_000,
};

/**
 * The units a proposal card's numbers are stored in (units slice 2d, CardUnits): the org's tags (the read
 * useCopilotTagWords shares) while the thread shows an alarm card in a unit, and the parameters the plant records (the
 * Plant limits page's read) while it shows a window card in a unit the org may read; `read` reads the one a card needs
 * when it is approved. A read that failed holds nothing: an alarm in the person's unit then stays off, and a window
 * goes as the card holds it, the limits route checking its unit.
 */
export function useCopilotCardUnits(tagsWanted: boolean, parametersWanted: boolean): CopilotCardUnits {
  const qc = useQueryClient();
  const live = useLiveEnabled();
  const tags = useQuery({ ...TAGS_QUERY, enabled: live && tagsWanted });
  const parameters = useQuery({ ...PARAMETERS_QUERY, enabled: live && parametersWanted });
  const tagItems = tags.data?.items;
  const tagsFailed = tags.isError;
  const params = parameters.data;
  const paramsFailed = parameters.isError;
  return useMemo(() => {
    const lookups = cardUnitsOf({
      tags: tagItems ? tagWordsOf(tagItems) : tagsFailed ? {} : undefined,
      parameters: params !== undefined ? (params?.parameters ?? null) : paramsFailed ? null : undefined,
    });
    const read = async (need: "tag" | "condition"): Promise<CardLookups> => {
      const cachedTags = qc.getQueryData<OrgTags>(ORG_TAG_CATALOG_KEY);
      const cachedParams = qc.getQueryData<WindowParameters>(PROCESS_WINDOW_PARAMETERS_KEY);
      let words = cachedTags ? tagWordsOf(cachedTags.items) : undefined;
      let list = cachedParams === undefined ? undefined : (cachedParams?.parameters ?? null);
      if (need === "tag") {
        words = await qc.fetchQuery(TAGS_QUERY).then((d) => tagWordsOf(d.items), () => ({}));
      } else {
        list = await qc.fetchQuery(PARAMETERS_QUERY).then((d) => d?.parameters ?? null, () => null);
      }
      return cardUnitsOf({ tags: words, parameters: list });
    };
    return { ...lookups, read };
  }, [qc, tagItems, tagsFailed, params, paramsFailed]);
}
